import { useEffect, useMemo, useState } from "react";
import { useStyles } from "./MillDetailsWeather.styles";
import Clear from "./images/Clear.jpg";
import Clouds from "./images/Clouds.jpg";
import Drizzle from "./images/Drizzle.jpg";
import Rain from "./images/Rain.jpg";
import Snow from "./images/Snow.jpg";
import Thunderstorm from "./images/Thunderstorm.jpg";
import Unknow from "./images/Unknow.jpg";
import { MillDetailEntity } from "entities/MillDetail/MillDetailEntity";
import { Alert, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/es";
import { IWeather } from "./MillDetailsWeatherInterfaces";

dayjs.extend(utc);
dayjs.extend(timezone);

const weatherPicture = {
    Clear: Clear,
    Clouds: Clouds,
    Drizzle: Drizzle,
    Rain: Rain,
    Snow: Snow,
    Thunderstorm: Thunderstorm,
    Unknow: Unknow,
};

const MillDetailsWeather = () => {
    const classes = useStyles();
    const { millId } = useParams();

    const { countryCode, zipCode, timezone } = MillDetailEntity.GetCorporateZipcodeAndCountry(Number(millId));
    const [weatherInformation, setWeatherInformation] = useState<IWeather | null>(null);
    const [error, setError] = useState<any>(null);
    const { t, i18n } = useTranslation();

    const locale = useMemo(() => i18n.language.slice(0, 2).toLowerCase(), [i18n.language]);

    useEffect(() => {
        if (countryCode && zipCode) {
            const getWeather = async () => {
                try {
                    const weatherDataResponse = await fetch(
                        `https://api.openweathermap.org/data/2.5/weather?zip=${zipCode},${countryCode.toLowerCase()}&units=imperial&lang=${locale}&appid=${
                            process.env.REACT_APP_OPEN_WEATHER_API_KEY
                        }`,
                    );
                    const weatherData = await weatherDataResponse.json();
                    setWeatherInformation(weatherData);
                } catch (error) {
                    setError(error);
                }
            };
            getWeather();
        }
    }, [countryCode, i18n.language, locale, zipCode]);

    const temperatureCelsius = useMemo(() => {
        if (weatherInformation?.main?.temp) {
            return Math.round((weatherInformation.main.temp - 32) * (5 / 9));
        }
        return null;
    }, [weatherInformation]);

    const lastUpdate = useMemo(() => {
        if (weatherInformation?.dt) {
            const date = new Date(weatherInformation.dt * 1000);

            return dayjs(date).tz(timezone).locale(locale).format("DD MMM hh:mm a");
        }
        return null;
    }, [locale, timezone, weatherInformation]);

    if (error) {
        return (
            <div className={`${classes.mainWrapper} ${classes.mainWrapperLoading}`}>
                <Alert severity="error">{t("locations.mill_details.mill_weather.error")}</Alert>
            </div>
        );
    }

    if (!weatherInformation) {
        return (
            <div className={`${classes.mainWrapper} ${classes.mainWrapperLoading}`}>
                <CircularProgress color="info" />
            </div>
        );
    }
    return (
        <div className={classes.mainWrapper}>
            <div className={classes.title}>{t("locations.mill_details.mill_weather.title")}</div>
            <div className={classes.weatherWrapper}>
                <div className={classes.weatherImageAndMainInfoWrapper}>
                    <div className={classes.weatherImage}>
                        <img
                            src={weatherPicture[weatherInformation?.weather?.[0]?.main as keyof typeof weatherPicture]}
                            alt="weather"
                        />
                    </div>
                    <div className={classes.weatherMainInfoWrapper}>
                        <div className={classes.weatherMainInfo}>{weatherInformation?.weather?.[0]?.description}</div>
                        <div className={classes.weatherMainInfoTemperature}>{weatherInformation?.main?.temp}°F</div>
                        <div className={classes.weatherMainInfoTemperatureCelsius}>{temperatureCelsius}°C</div>
                    </div>
                </div>
                <div className={classes.weatherDetailsWrapper}>
                    <div className={classes.weatherDetailsLabel}>
                        {t("locations.mill_details.mill_weather.humidity")}
                    </div>
                    <div className={classes.weatherDetailsValue}>{weatherInformation?.main?.humidity}%</div>
                    <div className={classes.weatherDetailsLabel}>
                        {t("locations.mill_details.mill_weather.wind_speed")}
                    </div>
                    <div className={classes.weatherDetailsValue}>{weatherInformation?.wind?.speed} mph</div>
                    <div className={classes.weatherDetailsLabel}>
                        {t("locations.mill_details.mill_weather.pressure")}
                    </div>
                    <div className={classes.weatherDetailsValue}>{weatherInformation?.main?.pressure} hPa</div>
                    <div className={classes.weatherDetailsLabel}>
                        {t("locations.mill_details.mill_weather.visibility")}
                    </div>
                    <div className={classes.weatherDetailsValue}>{weatherInformation?.visibility} mi</div>
                    <div className={classes.weatherDetailsLabel}>
                        {t("locations.mill_details.mill_weather.last_update")}
                    </div>
                    <div className={classes.weatherDetailsValue}>{lastUpdate}</div>
                </div>
            </div>
        </div>
    );
};

export default MillDetailsWeather;
